/**
 * Everything the CMS can work out for itself from what an editor has already
 * typed.
 *
 * These are pure string functions with no React, no DOM, and no Firebase, which
 * is why they live apart from the fields that call them: the rules for what a
 * slug or a reading time *is* are worth testing directly, and a rule that can
 * only be exercised by mounting a form is a rule nobody tests.
 *
 * None of this is authoritative. Every value produced here is written into a
 * normal, editable field and can be overwritten; the publish gate in
 * `@zennopay/schema` still decides what is allowed out.
 */

/** Matches `VITE_SITE_ORIGIN` in `.env`, which the fields pass in explicitly. */
export const DEFAULT_SITE_ORIGIN = "https://zennopay.com";

/* A slug is a URL segment, not a summary. Past about this length it stops
   helping a reader and starts truncating in search results. */
const MAX_SLUG_LENGTH = 72;

/** Google truncates titles past roughly here, and so does the publish gate. */
export const SEO_TITLE_MAX = 60;
/** Same, for descriptions. */
export const META_DESCRIPTION_MAX = 160;

/**
 * How long a hub card's summary runs before it starts wrapping badly.
 *
 * A little longer than the meta description budget on purpose: the excerpt is
 * the source the description is clamped from, and clamping a sentence that was
 * already clamped loses a second clause for no reason.
 */
export const EXCERPT_MAX = 200;

/**
 * The direct answer's word band.
 *
 * Transcribed from `DIRECT_ANSWER_MIN_WORDS` / `DIRECT_ANSWER_MAX_WORDS` in
 * `packages/schema/src/publish-gate.ts`, which does not export them. The gate
 * is what decides; these exist so the form can show an editor where they are
 * against it while they type rather than after they press publish. If the two
 * ever disagree, the gate wins and this is the bug.
 */
export const DIRECT_ANSWER_MIN_WORDS = 40;
export const DIRECT_ANSWER_MAX_WORDS = 70;

/** Mid-range of the usual 200-250 estimates. */
const WORDS_PER_MINUTE = 225;

/**
 * A URL-safe segment.
 *
 * Diacritics are folded rather than dropped so "Türkiye" becomes `turkiye` and
 * not `trkiye`, and apostrophes are removed rather than replaced so "payer's"
 * becomes `payers` and not `payer-s`.
 */
export function slugify(input: string): string {
    const slug = (input ?? "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/['\u2018\u2019]/g, "")
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    if (slug.length <= MAX_SLUG_LENGTH) return slug;

    /* Cut on a word boundary. A slug ending mid-word reads like a truncation
       bug to anyone who sees the URL. */
    const cut = slug.slice(0, MAX_SLUG_LENGTH + 1);
    const lastHyphen = cut.lastIndexOf("-");
    return (lastHyphen > 0 ? cut.slice(0, lastHyphen) : slug.slice(0, MAX_SLUG_LENGTH))
        .replace(/-+$/, "");
}

/** The public URL a guide with this slug will be served from. */
export function canonicalUrlFor(slug: string, origin: string = DEFAULT_SITE_ORIGIN): string {
    const trimmed = (slug ?? "").trim();
    if (!trimmed) return "";
    return `${origin.replace(/\/+$/, "")}/guides/${trimmed}/`;
}

/**
 * Markdown reduced to the words a reader actually reads.
 *
 * Code blocks are removed rather than counted: a 200-line snippet is not three
 * minutes of reading, and counting it would inflate every technical guide.
 */
export function plainTextFromMarkdown(markdown: string): string {
    return (markdown ?? "")
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/~~~[\s\S]*?~~~/g, " ")
        .replace(/`[^`]*`/g, " ")
        .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
        .replace(/\[\^[^\]]*\]/g, " ")
        .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/<[^>]+>/g, " ")
        .replace(/^\s{0,3}>+/gm, " ")
        .replace(/^\s*[-*+]\s+/gm, " ")
        .replace(/^\s*\d+\.\s+/gm, " ")
        .replace(/^\s*#{1,6}\s*/gm, " ")
        .replace(/^\s*\|/gm, " ")
        .replace(/[*_~|]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

export function countWords(text: string): number {
    const trimmed = (text ?? "").trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).filter(Boolean).length;
}

/**
 * Minutes, from the body's word count.
 *
 * Returns 0 for an empty body so the caller can leave the field alone rather
 * than write a confident "1 min" for a guide nobody has written yet.
 */
export function readingTimeMinutes(body: string): number {
    const words = countWords(plainTextFromMarkdown(body));
    if (words === 0) return 0;
    return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE));
}

/* -------------------------------------------------------------- keywords -- */

/**
 * Words that carry no topic. Kept deliberately short: this list exists to stop
 * "the" and "for" being proposed as keywords, not to do linguistics.
 */
const STOPWORDS = new Set([
    "a", "about", "after", "all", "also", "an", "and", "any", "are", "as", "at",
    "be", "because", "been", "before", "being", "between", "both", "but", "by",
    "can", "cannot", "could", "did", "do", "does", "doing", "done", "down",
    "each", "even", "every", "for", "from", "further", "get", "gets", "had",
    "has", "have", "having", "he", "her", "here", "hers", "him", "his", "how",
    "however", "i", "if", "in", "into", "is", "it", "its", "itself", "just",
    "like", "make", "makes", "many", "may", "me", "might", "more", "most",
    "much", "must", "my", "need", "no", "nor", "not", "now", "of", "off", "on",
    "once", "one", "only", "or", "other", "others", "our", "ours", "out",
    "over", "own", "per", "same", "she", "should", "since", "so", "some",
    "such", "than", "that", "the", "their", "theirs", "them", "then", "there",
    "these", "they", "this", "those", "through", "to", "too", "two", "under",
    "until", "up", "use", "used", "uses", "using", "very", "was", "we", "were",
    "what", "when", "where", "which", "while", "who", "whom", "why", "will",
    "with", "within", "without", "would", "you", "your", "yours"
]);

/** Long bodies are sampled rather than scanned whole; this runs on keystrokes. */
const KEYWORD_SCAN_LIMIT = 20000;

const MAX_PHRASE_WORDS = 3;
/* Two, not three: this domain is full of two-letter terms that are the whole
   subject — QR, FX, PH. The stopword list already removes the "of"s and "is"s
   that a longer minimum would have been catching. */
const MIN_TOKEN_LENGTH = 2;

/** A title term is worth this many body mentions. It is what the guide is about. */
const TITLE_WEIGHT = 3;

/**
 * Runs of consecutive meaningful words.
 *
 * Splitting on stopwords and punctuation is what keeps "rails in southeast" out
 * of the candidate list: a phrase that straddles a stopword is not a phrase.
 */
function contentRuns(text: string): string[][] {
    const runs: string[][] = [];
    let current: string[] = [];

    for (const raw of text.toLowerCase().split(/[^a-z0-9'\u2019-]+/)) {
        const token = raw.replace(/['\u2018\u2019]/g, "").replace(/^-+|-+$/g, "");
        const usable =
            token.length >= MIN_TOKEN_LENGTH && !STOPWORDS.has(token) && !/^\d+$/.test(token);
        if (usable) {
            current.push(token);
        } else if (current.length > 0) {
            runs.push(current);
            current = [];
        }
    }
    if (current.length > 0) runs.push(current);
    return runs;
}

/** Every contiguous 1-to-3 word phrase inside the given runs. */
function phrasesIn(runs: string[][]): string[] {
    const phrases: string[] = [];
    for (const run of runs) {
        for (let start = 0; start < run.length; start++) {
            for (let length = 1; length <= MAX_PHRASE_WORDS && start + length <= run.length; length++) {
                phrases.push(run.slice(start, start + length).join(" "));
            }
        }
    }
    return phrases;
}

export interface KeywordSuggestions {
    /** Undefined when there is not enough text to suggest anything honestly. */
    primary?: string;
    secondary: string[];
}

/**
 * Keyword *suggestions*, from the words the guide already uses.
 *
 * This is frequency counting, not search-volume research, and the UI says so.
 * Its job is to stop an editor typing something the guide already told us,
 * not to pretend the CMS knows what people search for.
 *
 * A longer phrase beats a single word at equal frequency, because "qr payouts
 * southeast asia" is a keyword and "asia" is a topic.
 */
export function deriveKeywords(
    title: string,
    body: string,
    limit = 5
): KeywordSuggestions {
    const inTitle = count(phrasesIn(contentRuns(title ?? "")));
    const inBody = count(phrasesIn(
        contentRuns(plainTextFromMarkdown(body ?? "").slice(0, KEYWORD_SCAN_LIMIT))
    ));

    const scored: Array<[phrase: string, score: number]> = [];
    for (const phrase of new Set([...inTitle.keys(), ...inBody.keys()])) {
        const titleCount = inTitle.get(phrase) ?? 0;
        const bodyCount = inBody.get(phrase) ?? 0;

        /* A phrase that occurs once, in the body, in a guide nobody has read is
           a coincidence, not a keyword. Requiring either the title or a repeat
           is what keeps "beneficiary reference inside" off the list. */
        if (titleCount === 0 && bodyCount < 2) continue;

        const words = phrase.split(" ").length;
        scored.push([phrase, (bodyCount + TITLE_WEIGHT * titleCount) * words]);
    }

    /* Sorted by score, then by length, then alphabetically, so the same inputs
       always produce the same suggestions. An editor who retypes a character
       should not watch the keywords reshuffle. */
    const ranked = scored
        .sort((a, b) => {
            if (b[1] !== a[1]) return b[1] - a[1];
            const lengthDelta = b[0].split(" ").length - a[0].split(" ").length;
            if (lengthDelta !== 0) return lengthDelta;
            return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0;
        })
        .map(([phrase]) => phrase);

    const chosen: string[] = [];
    for (const phrase of ranked) {
        /* One idea per suggestion: "qr payout" and "qr payouts carries" are the
           same recommendation three times over. Singulars are folded in, or the
           plural would slip through as a separate idea. */
        const overlaps = chosen.some(
            (taken) => overlapKey(taken).includes(overlapKey(phrase))
                || overlapKey(phrase).includes(overlapKey(taken))
        );
        if (!overlaps) chosen.push(phrase);
        if (chosen.length > limit) break;
    }

    const [primary, ...secondary] = chosen;
    return { ...(primary ? { primary } : {}), secondary: secondary.slice(0, limit) };
}

function count(phrases: string[]): Map<string, number> {
    const counts = new Map<string, number>();
    for (const phrase of phrases) counts.set(phrase, (counts.get(phrase) ?? 0) + 1);
    return counts;
}

/**
 * A phrase reduced to what makes it the same idea as another: singulars folded
 * in, and padded so `includes` compares whole words rather than prefixes.
 */
const overlapKey = (phrase: string): string =>
    ` ${phrase
        .split(" ")
        .map((word) => (word.length > 3 && word.endsWith("s") ? word.slice(0, -1) : word))
        .join(" ")} `;

/* ------------------------------------------------------------------- seo -- */

/** Truncate on a word boundary, ellipsis included in the budget. */
export function clampToWords(text: string, max: number): string {
    const collapsed = (text ?? "").replace(/\s+/g, " ").trim();
    if (collapsed.length <= max) return collapsed;

    const cut = collapsed.slice(0, max - 1);
    const lastSpace = cut.lastIndexOf(" ");
    return `${(lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.–-]+$/, "")}…`;
}

/**
 * The `<title>` tag, from the guide's title.
 *
 * The brand suffix is added only when it fits inside the 60 characters search
 * results show. A suffix that pushes the actual subject off the end is worse
 * than no suffix.
 */
export function deriveSeoTitle(title: string, brand = "Zennopay"): string {
    const trimmed = (title ?? "").replace(/\s+/g, " ").trim();
    if (!trimmed) return "";

    const withBrand = `${trimmed} | ${brand}`;
    if (withBrand.length <= SEO_TITLE_MAX) return withBrand;
    if (trimmed.length <= SEO_TITLE_MAX) return trimmed;
    return clampToWords(trimmed, SEO_TITLE_MAX);
}

/**
 * The meta description, from the excerpt, falling back to the direct answer.
 *
 * Both are already written for a reader, which is the only honest source for a
 * sentence that shows up in search results.
 */
export function deriveMetaDescription(excerpt: string, directAnswer?: string): string {
    const source = ((excerpt ?? "").trim() || (directAnswer ?? "").trim());
    if (!source) return "";
    return clampToWords(source, META_DESCRIPTION_MAX);
}

/* --------------------------------------------------------------- excerpt -- */

/**
 * As many whole sentences as fit inside `max`.
 *
 * Whole sentences, because an excerpt is read by a human on a card and a
 * half-sentence with an ellipsis reads as a truncation bug. When even the first
 * sentence is too long there is nothing to preserve, and the word-boundary
 * clamp is the honest fallback.
 */
export function firstSentences(text: string, max: number): string {
    const collapsed = (text ?? "").replace(/\s+/g, " ").trim();
    if (!collapsed) return "";
    if (collapsed.length <= max) return collapsed;

    const sentences = collapsed.match(/[^.!?]*[.!?]+(?:\s+|$)|[^.!?]+$/g) ?? [collapsed];

    let kept = "";
    for (const sentence of sentences) {
        const next = kept + sentence;
        if (next.trim().length > max) break;
        kept = next;
    }

    return kept.trim() || clampToWords(collapsed, max);
}

/**
 * The hub card summary, from the answer the editor has already written.
 *
 * The direct answer first, because it is the one piece of prose in a guide that
 * is written to stand alone, which is exactly what a card needs. The body's
 * opening is the fallback for a guide whose answer is not written yet; it is a
 * worse summary but a real one, and the field says where it came from.
 *
 * Nothing is invented here. Every word returned is a word the editor typed.
 */
export function deriveExcerpt(directAnswer: string, body?: string): string {
    const answer = (directAnswer ?? "").trim();
    const source = answer || plainTextFromMarkdown(body ?? "");
    if (!source) return "";
    return firstSentences(source, EXCERPT_MAX);
}

/* -------------------------------------------------------- primary question -- */

/**
 * Words that already open a question, so the title only needs a question mark.
 */
const QUESTION_OPENERS = new Set([
    "what", "how", "why", "when", "where", "which", "who", "whom", "whose",
    "can", "could", "do", "does", "did", "is", "are", "was", "were",
    "should", "will", "would", "must", "may", "might", "has", "have"
]);

/* "AML for cross-border payouts" reads as a subject and a scope. Past about
   three words the left side stops being a subject and starts being a sentence,
   and "A practical guide for beginners" is not a requirements question. */
const MAX_SUBJECT_WORDS = 3;

/**
 * A question the title implies, for the editor to confirm or rewrite.
 *
 * This is a *suggestion*, never a silent substitution: it is written into the
 * ordinary `primaryQuestion` field, badged as automatic, and sits second in the
 * form where it cannot be missed. Left as-is it is still a fair statement of
 * what the guide is about, because every word in it came from the title.
 *
 * The title's own wording and capitalisation are carried across verbatim.
 * Lower-casing the first word would read better for "Licensing for money
 * transmitters" and would be wrong for "Thailand", and a suggestion that is
 * occasionally wrong about a proper noun is worse than one that is
 * occasionally over-capitalised, because only one of the two is obvious.
 */
export function deriveQuestionFromTitle(title: string): string {
    const collapsed = (title ?? "").replace(/\s+/g, " ").trim();
    if (!collapsed) return "";

    /* Already a question. Editors do write titles this way. */
    if (collapsed.endsWith("?")) return collapsed;

    const base = collapsed.replace(/[.,;:!\s]+$/, "");
    if (!base) return "";

    const words = base.split(" ");
    const opener = (words[0] ?? "").toLowerCase().replace(/[^a-z]/g, "");
    if (QUESTION_OPENERS.has(opener)) return `${base}?`;

    /* "<subject> for <scope>" is the shape most compliance titles take. */
    const scoped = /^(.+?)\s+for\s+(.+)$/.exec(base);
    if (scoped && (scoped[1] ?? "").split(" ").length <= MAX_SUBJECT_WORDS) {
        return `What are the ${scoped[1]} requirements for ${scoped[2]}?`;
    }

    return `What do you need to know about ${base}?`;
}
